import { Arrays, isDefined, Option, panic, Progress, tryCatch, UUID } from "@opendaw/lib-std";
import { AudioData } from "@opendaw/lib-dsp";
import { Promises } from "@opendaw/lib-runtime";
import { SamplePeaks } from "@opendaw/lib-fusion";
import { AudioFileBox } from "@opendaw/studio-boxes";
import { AssetService } from "../AssetService";
import { FilePickerAcceptTypes } from "../FilePickerAcceptTypes";
import { WavFile } from "@opendaw/lib-dsp";
import { Workers } from "../Workers";
import { SampleStorage } from "./SampleStorage";
import { FactoryCatalog } from "../FactoryCatalog";
export class SampleService extends AssetService {
    audioContext;
    bpmDetector;
    static AudioExtensions = new Set(["wav", "wave", "aif", "aiff", "aifc", "mp3", "ogg", "oga", "opus", "flac", "m4a", "aac", "webm", "weba"]);
    namePlural = "Samples";
    nameSingular = "Sample";
    boxType = AudioFileBox;
    filePickerOptions = FilePickerAcceptTypes.WavFiles;
    constructor(audioContext, bpmDetector) {
        super();
        this.audioContext = audioContext;
        this.bpmDetector = bpmDetector;
    }
    acceptsFile(file) {
        return file.type.startsWith("audio/") || SampleService.AudioExtensions.has(AssetService.extensionOf(file));
    }
    async importRecording(uuid, audioData, bpm, name = "Recording") {
        // A sample MUST have a positive length. A zero-frame take would become a duration-0 sample and, once
        // dropped, a duration-0 region that later trips validateTrack ("duration must be positive"). Reject it
        // at the door so the invariant "every sample has duration > 0" holds for every downstream consumer.
        if (audioData.numberOfFrames === 0) {
            return panic(`Cannot import recording '${name}': the take is empty (0 frames).`);
        }
        const arrayBuffer = WavFile.encodeFloats({
            frames: audioData.frames.slice(),
            numberOfFrames: audioData.numberOfFrames,
            numberOfChannels: audioData.numberOfChannels,
            sampleRate: audioData.sampleRate
        });
        return this.importFile({ uuid, name, bpm, arrayBuffer, origin: "recording" });
    }
    async importFile({ uuid, name, bpm, arrayBuffer, progressHandler = Progress.Empty, origin = "import" }, transformMeta) {
        console.debug(`importSample '${name}' (${arrayBuffer.byteLength >> 10}kb)`);
        uuid ??= await UUID.sha256(arrayBuffer);
        const audioData = await this.#decodeAudio(arrayBuffer);
        // Empty/undecodable audio yields 0 frames -> duration 0. Such a sample creates duration-0 regions that
        // later panic in validateTrack. Enforce "a sample has duration > 0" here, the single source of every
        // audio region, instead of guarding every consumer downstream.
        if (audioData.numberOfFrames === 0) {
            return panic(`Cannot import '${name}': the audio is empty (0 frames).`);
        }
        const duration = audioData.numberOfFrames / audioData.sampleRate;
        const shifts = SamplePeaks.findBestFit(audioData.numberOfFrames);
        // A caller-supplied bpm is authoritative (a recording knows the tempo it was captured at), so detection
        // only runs when the tempo is genuinely unknown and only then claims a share of the progress.
        const optGivenBpm = Option.wrap(bpm);
        const [peakProgress, bpmProgress] = optGivenBpm.isEmpty()
            ? Progress.split(progressHandler, 2)
            : [progressHandler, Progress.Empty];
        const peaks = await Workers.Peak.generateAsync(peakProgress, shifts, audioData.frames, audioData.numberOfFrames, audioData.numberOfChannels);
        // `None` stores 0, the established "unknown" sentinel that keeps the material in seconds.
        const optBpm = optGivenBpm.isEmpty()
            ? await this.bpmDetector.detect(audioData, bpmProgress)
            : optGivenBpm;
        const meta = {
            bpm: optBpm.unwrapOrElse(0),
            name: name ?? "Unnnamed",
            duration,
            sample_rate: audioData.sampleRate,
            origin
        };
        if (isDefined(transformMeta)) {
            await transformMeta(meta, audioData);
        }
        const sample = { uuid: UUID.toString(uuid), ...meta };
        await SampleStorage.get().save({ uuid, audio: audioData, peaks, meta });
        this.notifier.notify([sample, audioData]);
        return sample;
    }
    async collectAllFiles() {
        console.debug("[ProjectLoad] sample catalogue requested");
        const stock = await FactoryCatalog.get().samples();
        console.debug(`[ProjectLoad] sample catalogue received (${stock.length})`);
        const local = await SampleStorage.get().list();
        console.debug(`[ProjectLoad] local samples listed (${local.length})`);
        // Cleanup migration: a historical bug let zero-length audio become a duration-0 sample, which then
        // created duration-0 regions that crash validateTrack. The import guard stops new ones; purge any
        // already saved (and any with a NaN/negative duration) so they can never be dropped again. Self-heals
        // on every list, so no run-once flag is needed. `!(duration > 0)` also catches NaN.
        const valid = local.filter(sample => sample.duration > 0);
        if (valid.length < local.length) {
            const invalid = local.filter(sample => !(sample.duration > 0));
            console.warn(`Purging ${invalid.length} zero-duration sample(s):`, invalid.map(sample => sample.uuid));
            const storage = SampleStorage.get();
            await Promise.all(invalid.map(sample => Promises.tryCatch(storage.deleteItem(UUID.parse(sample.uuid)))));
        }
        return Arrays.merge(stock, valid, (sample, { uuid }) => sample.uuid === uuid);
    }
    async #decodeAudio(arrayBuffer) {
        const wavResult = tryCatch(() => WavFile.decodeFloats(arrayBuffer));
        if (wavResult.status === "success") {
            return wavResult.value;
        }
        console.debug("decoding with web-api-api (fallback)");
        const { status, value: audioBuffer } = await Promises.tryCatch(this.audioContext.decodeAudioData(arrayBuffer));
        if (status === "rejected") {
            return Promise.reject(new Error("Could not decode audio file"));
        }
        const audioData = AudioData.create(audioBuffer.sampleRate, audioBuffer.length, audioBuffer.numberOfChannels);
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            audioData.frames[channel].set(audioBuffer.getChannelData(channel));
        }
        return audioData;
    }
}
